import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { cx } from './cx';

/* ------------------------------------------------------------------------------------------------ */
/* Card and Panel                                                                                    */
/* ------------------------------------------------------------------------------------------------ */

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  as?: 'section' | 'article' | 'div' | 'li';
  title?: ReactNode;
  eyebrow?: ReactNode;
  /** Right side of the header (a badge, a button). */
  actions?: ReactNode;
  raised?: boolean;
  /** Inner padding (default true). */
  pad?: boolean;
  children?: ReactNode;
}

/** A content surface. With `title` it renders a header row with an h3. */
export function Card({ as: Tag = 'section', title, eyebrow, actions, raised, pad = true, className, children, ...rest }: CardProps) {
  return (
    <Tag className={cx('card', pad && 'card--pad', raised && 'card--raised', className)} {...rest}>
      {(title || actions || eyebrow) && (
        <header className="card__header">
          <div className="grow">
            {eyebrow && <p className="eyebrow card__eyebrow">{eyebrow}</p>}
            {title && <h3 className="card__title">{title}</h3>}
          </div>
          {actions}
        </header>
      )}
      {children}
    </Tag>
  );
}

export interface CardButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  children?: ReactNode;
}

/** A whole card that is one tap target (lists of choices, name pickers). */
export function CardButton({ selected, className, children, type = 'button', ...rest }: CardButtonProps) {
  return (
    <button type={type} className={cx('card card--pad card--interactive', selected && 'card--selected', className)} aria-pressed={selected} {...rest}>
      {children}
    </button>
  );
}

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  icon?: IconName;
  actions?: ReactNode;
  children?: ReactNode;
  bodyClassName?: string;
}

/** A framed section with a title bar, used for console and desk regions. */
export function Panel({ title, icon, actions, children, className, bodyClassName, ...rest }: PanelProps) {
  return (
    <section className={cx('panel', className)} {...rest}>
      <header className="panel__header">
        {icon && <Icon name={icon} size={20} />}
        <h2 className="panel__title">{title}</h2>
        {actions}
      </header>
      <div className={cx('panel__body', bodyClassName)}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------------------------------------ */
/* Badge                                                                                             */
/* ------------------------------------------------------------------------------------------------ */

export type Tone = 'neutral' | 'accent' | 'info' | 'good' | 'warn' | 'bad';

export interface BadgeProps {
  tone?: Tone;
  solid?: boolean;
  dot?: boolean;
  icon?: IconName;
  children: ReactNode;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', solid, dot, icon, children, className, title }: BadgeProps) {
  return (
    <span className={cx('badge', tone !== 'neutral' && `badge--${tone}`, solid && 'badge--solid', className)} title={title}>
      {dot && <span className="badge__dot" aria-hidden />}
      {icon && <Icon name={icon} size={14} strokeWidth={2.2} />}
      {children}
    </span>
  );
}

/** The visible "AI" marker for computer-run nations. */
export function AiBadge() {
  return (
    <Badge tone="info" title="This nation is run by the computer">
      AI
    </Badge>
  );
}

/* ------------------------------------------------------------------------------------------------ */
/* Callout                                                                                           */
/* ------------------------------------------------------------------------------------------------ */

export interface CalloutProps {
  tone?: 'info' | 'good' | 'warn' | 'bad';
  icon?: IconName;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Announce politely to screen readers when it appears. */
  live?: boolean;
}

export function Callout({ tone = 'info', icon, title, children, className, live }: CalloutProps) {
  const fallback: IconName = tone === 'good' ? 'check' : tone === 'info' ? 'info' : 'warning';
  return (
    <div className={cx('callout', tone !== 'info' && `callout--${tone}`, className)} role={live ? (tone === 'bad' ? 'alert' : 'status') : undefined}>
      <Icon name={icon ?? fallback} className="callout__icon" size={20} />
      <div className="grow">
        {title && <p style={{ fontWeight: 650 }}>{title}</p>}
        {children && <div className="muted">{children}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------ */
/* Empty state, skeleton, spinner                                                                    */
/* ------------------------------------------------------------------------------------------------ */

export interface EmptyStateProps {
  icon?: IconName;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ icon = 'info', title, children, action }: EmptyStateProps) {
  return (
    <div className="empty">
      <div className="empty__icon">
        <Icon name={icon} size={28} />
      </div>
      <p className="empty__title">{title}</p>
      {children && <div className="empty__text">{children}</div>}
      {action}
    </div>
  );
}

export interface SkeletonProps {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  radius?: CSSProperties['borderRadius'];
  className?: string;
}

export function Skeleton({ width = '100%', height = 16, radius, className }: SkeletonProps) {
  return <span className={cx('skeleton', className)} style={{ width, height, borderRadius: radius }} aria-hidden />;
}

/** A few skeleton lines, announced once as "Loading". */
export function SkeletonBlock({ lines = 3 }: { lines?: number }) {
  return (
    <div className="stack" style={{ gap: 10 }} role="status" aria-label="Loading">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '60%' : '100%'} />
      ))}
    </div>
  );
}

export function Spinner({ label = 'Loading', size = 22 }: { label?: string; size?: number }) {
  return <span className="spinner" role="status" aria-label={label} style={{ width: size, height: size }} />;
}

/** Full-area centered loading state. */
export function LoadingScreen({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading-screen" role="status">
      <span className="spinner" aria-hidden style={{ width: 32, height: 32, color: 'var(--amber)' }} />
      <p className="muted">{label}</p>
    </div>
  );
}
